import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {
    KAL_DIALOG_DATA,
    KalDate,
    KalDialogConfig,
    KalDialogRef,
    KalDialogService,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { FormValidator } from 'src/app/validators/form-validator';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { catchError, delay, distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs/operators';
import isEqual from 'lodash-es/isEqual';
import { Observable, of, throwError } from 'rxjs';
import { ExerciceInfos, MutationGetSoldePmvBenefArgs, MutationSolderPmvArgs } from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { getISODate, getKalDateFromObject } from 'src/app/utils/date.utils';
import { ExerciceState } from 'src/app/_shared/stores/exercices/exercices.state';
import { GetListeExerciceInfosAction } from 'src/app/_shared/stores/exercices/exercices.action';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { UserState } from 'src/app/_user/store/user/user.state';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';

export interface SharedAllocationSettleLoadsDialogData {
    accepted?: boolean;
    allocationId: number
};

enum EtatClotureExerciceEnum {
    Aucun = 'Aucun',
    Cloture = 'Cloture',
    EnCoursCloture = 'EnCoursCloture',
    ExerciceNonExistant = 'ExerciceNonExistant',
};

type StatusMessage = { code: EtatClotureExerciceEnum, paragraphs: string[] };

@Component({
    selector: 'app-shared-allocation-settle-loads-dialog',
    templateUrl: './shared-allocation-settle-loads-dialog.component.html',
    styleUrls: ['./shared-allocation-settle-loads-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationSettleLoadsDialogComponent extends BaseComponent implements OnInit {

    form: UntypedFormGroup;

    readonly beneficiarySelection = {
        one: 'one',
        all: 'all'
    }

    balanceAmount: number = null;

    private lastDate: KalDate = null;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: UntypedFormBuilder,
                private readonly snackbarService: KalSnackbarService,
                private readonly dialogService: KalDialogService,
                private readonly graphqlService: GraphqlService,
                private readonly dialogRef: KalDialogRef<SharedAllocationSettleLoadsDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: SharedAllocationSettleLoadsDialogData) {
        super(cdr);
    }

    get isMultiSelection(): boolean {
        return this.form.value.Sur === this.beneficiarySelection.all;
    }

    private get exercisesList(): ExerciceInfos[] {
        return this.snapshot(ExerciceState.exercicesInfos) || [];
    }

    private get beneficiaryControl(): UntypedFormControl {
        return this.form.get('IdBenef') as UntypedFormControl;
    }

    private get dateControl(): UntypedFormControl {
        return this.form.get('DateValeur') as UntypedFormControl;
    }

    private get currentDelegationId(): number {
        return this.snapshot(UserState.currentDelegation).Id;
    }

    submit(): void {
        if (this.form.invalid || this.loading) {
            return;
        }

        const {IdBenef, DateValeur} = this.form.value;

        let config = new KalDialogConfig<ConfirmDialogData>({
            title: 'Information',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        });

        if (!!IdBenef && this.balanceAmount === 0) {
            config = {
                ...config,
                data: {
                    body: 'Le porte-monnaie virtuel de ce bénéficiaire est déjà soldé.',
                    cancelLabel: 'Fermer'
                }
            };
        } else if (DateValeur.isAfter(new KalDate())) {
            config = {
                ...config,
                data: {
                    body: 'La date de reprise de charge doit être inferieure ou égale à la date du jour.',
                    cancelLabel: 'Fermer'
                }
            };
        } else {
            config = {
                ...config,
                title: 'Confirmation reprise de charge',
                data: {
                    body: 'Souhaitez-vous effectuer cette reprise de charge',
                    cancelLabel: 'Fermer',
                    submitLabel: 'Continuer'
                }
            };
        }

        const action$: Observable<ConfirmDialogData> = config.data
            ? this.dialogService.open(ConfirmDialogComponent, config).afterClosed
            : of({accepted: true});

        this.watch$ = action$.pipe(
            filter(data => data?.accepted),
            switchMap(() => {
                this.loading = true;
                return this.settle$()
            }),
            tap(() => {
                this.snackbarService.open({title: 'Reprise de charge effectuée.'});
                this.dialogRef.close({accepted: true});
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    cancel(): void {
        this.dialogRef.close({accepted: false});
    }

    private checkDate$(date: KalDate): Observable<ConfirmDialogData> {
        if (!date) {
            this.dateControl.reset(this.lastDate, {emitEvent: false});
            return of(null);
        }

        if (!this.lastDate || !this.lastDate.isSame(date)) {
            const checkExercice = this.checkStatusExercise(date);

            let config = new KalDialogConfig<ConfirmDialogData>({
                title: 'Information',
                hasBackdrop: true,
                backdropClass: 'cdk-overlay-dark-backdrop',
                data: {
                    cancelLabel: 'Fermer'
                }
            });

            switch (checkExercice.code) {
                case EtatClotureExerciceEnum.Aucun:
                    this.lastDate = date;
                    break;

                case EtatClotureExerciceEnum.Cloture :
                    this.dateControl.reset(this.lastDate, {emitEvent: false});

                    config.title = 'Information';
                    config.data.paragraphs = checkExercice.paragraphs;

                    return this.dialogService.open(ConfirmDialogComponent, config).afterClosed;

                case EtatClotureExerciceEnum.EnCoursCloture :

                    config.title = 'Clôture en cours';
                    config.data.paragraphs = checkExercice.paragraphs;
                    config.data.submitLabel = 'Continuer';

                    return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
                        tap(data => {
                            if (!!data?.accepted) {
                                this.lastDate = date;
                            } else {
                                this.dateControl.reset(this.lastDate, {emitEvent: false})
                            }
                        })
                    );
            }

        }

        return of(null);
    }

    private initSubscription(): void {
        this.watch$ = this.dateControl.valueChanges.pipe(
            delay(1),
            filter((date) => !!date),
            switchMap(date => this.checkDate$(date)),
            switchMap(() => this.getVirtualWalletBalance$()),
        );

        this.watch$ = this.beneficiaryControl.valueChanges.pipe(
            delay(1),
            switchMap(() => this.getVirtualWalletBalance$())
        );

        const beneficiarySelectedControl = this.form.get('Sur');
        this.watch$ = beneficiarySelectedControl.valueChanges.pipe(
            startWith(beneficiarySelectedControl.value),
            distinctUntilChanged(isEqual),
            tap(value => {
                const validatorsList = [FormValidator.int];
                if (value === this.beneficiarySelection.one) {
                    validatorsList.push(FormValidator.isNotNull)
                }
                this.beneficiaryControl.clearValidators();
                this.beneficiaryControl.addValidators(validatorsList);
                this.beneficiaryControl.reset(null, {emitEvent: false});
            })
        );
    }

    private settle$(): Observable<string> {
        const {IdBenef, DateValeur} = this.form.value;

        return this.graphqlService.mutateOne<'SolderPMV', MutationSolderPmvArgs>({
            mutation: gql`
                mutation SolderPMV($contexte: ContextInfosInput,
                    $IdAbondement: Float,
                    $IdUser: Float
                    $IdBenef: Float,
                    $DateValeur: DateTime
                ) {
                    SolderPMV(
                        contexte: $contexte
                        IdAbondement: $IdAbondement,
                        IdUser : $IdUser,
                        IdBenef: $IdBenef,
                        DateValeur: $DateValeur
                    )
                }
            `,
            variables: {
                IdAbondement: this.data.allocationId,
                IdBenef,
                IdUser: this.currentDelegationId,
                DateValeur: getISODate(DateValeur)
            }
        });
    }

    private getVirtualWalletBalance$(): Observable<number> {
        const {IdBenef, DateValeur} = this.form.value;
        if ([IdBenef, DateValeur].some(e => !e)) {
            return of(null);
        }

        this.loading = true;
        return this.graphqlService.mutateOne<'GetSoldePMVBenef', MutationGetSoldePmvBenefArgs>({
            mutation: gql`
                mutation GetSoldePMVBenef($contexte: ContextInfosInput,
                    $IdAbondement: Float,
                    $IdBenef: Float,
                    $DateValeur: DateTime
                ) {
                    GetSoldePMVBenef(
                        contexte: $contexte
                        IdAbondement: $IdAbondement,
                        IdBenef: $IdBenef,
                        DateValeur: $DateValeur
                    )
                }
            `,
            variables: {
                IdAbondement: this.data.allocationId,
                IdBenef,
                DateValeur: getISODate(DateValeur)
            }
        }).pipe(
            tap(GetSoldePMVBenef => {
                this.loading = false;
                this.balanceAmount = GetSoldePMVBenef || 0;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private updateForm(): void {
        this.form.patchValue({
            DateValeur: new KalDate(),
            Sur: this.beneficiarySelection.one,
            IdBenef: null
        });
    }

    private checkStatusExercise(date: KalDate): StatusMessage {
        const exerciceConcerne = this.exercisesList.find((ex) =>
            ex.IdDelegation === this.currentDelegationId
            && getKalDateFromObject(ex,'DateDebutExercice').isSameOrBefore(date)
            && getKalDateFromObject(ex,'DateFinExercice').isSameOrAfter(date)
        );

        return this.getStatusMessage(exerciceConcerne);
    }

    private getStatusMessage(exercise: ExerciceInfos): StatusMessage {
        if (!exercise) {
            return {
                code: EtatClotureExerciceEnum.ExerciceNonExistant,
                paragraphs: ['La date que vous avez sélectionnée ne correspond à aucun exercice non clôturé.']
            };
        }

        if (!!exercise.IsCloture) {
            return {
                code: EtatClotureExerciceEnum.EnCoursCloture,
                paragraphs: ['La date que vous avez sélectionnée correspond à un exercice clôturé, vous ne pouvez pas entrer de nouvelles écritures avec cette date.']
            };
        }

        if (!!exercise.IsEnCoursDeCloture) {
            return {
                code: EtatClotureExerciceEnum.EnCoursCloture,
                paragraphs: ['La date que vous avez sélectionnée correspond à un exercice en cours de clôture.',
                    'Votre écriture pourrait influencer cette clôture.',
                    'Souhaitez-vous continuer ?']
            };
        }

        return {
            code: EtatClotureExerciceEnum.Aucun,
            paragraphs: ['']
        };
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            DateValeur: [null, [FormValidator.date, FormValidator.isNotNull]],
            Sur: [this.beneficiarySelection.one, [FormValidator.isNotNull]],
            IdBenef: [null, [FormValidator.int]]
        });

        if (arrayEmpty(this.exercisesList)) {
            this.loading = true;
            this.watch$ = this.dispatch$(new GetListeExerciceInfosAction()).pipe(
                wheneverEnd(() => this.loading = false)
            );
        }

        this.updateForm();

        this.initSubscription();
    }
}
